<template>
    <div class="appGrey" style="min-height: 100%">
        {{ computedInit }}

        <!--Stewards-->
        <div>
            <!--Label-->
            <app-text size="medium-bold">Stewards</app-text>

            <!--Form-->
            <div class="d-flex align-center justify-space-between mt-4">
                <!--Stewards-->
                <app-form-field
                    form-type="autoComplete"
                    :disabled="!isStewardsEditable"
                    :items="computedStewardsTableData"
                    :item-text="(item) => `${item.userName} (${item.userInternalIdNumber})`"
                    label="Available Stewards"
                    :multiple="true"
                    :return-object="true"
                    :small-chips="true"
                    style="width: 100%"
                    v-model="tempTeamStewards"
                    @change="validateStewardSelection"
                />

                <!--Save Button-->
                <app-btn
                    v-if="isStewardsEditable"
                    @click.native="saveStewards(tempTeamStewards)"
                    class="ml-4"
                    color="green"
                    :disabled="!tempTeamStewards.length || hasValidationErrors"
                    icon="save"
                    label="Save"
                />

                <!--Edit Button-->
                <edit-icon @click.native="editStewards" :isActive="isStewardsEditable" />
            </div>

            <!--Validation Error Message-->
            <div v-if="validationError" class="mt-2">
                <app-text color="red" size="small">{{ validationError }}</app-text>
            </div>

            <!--Table-->
            <v-data-table class="appWhite rounded-lg mt-4" :items="teamStewards" :headers="tableHeaders">
                <!--Steward-->
                <template v-slot:item.userName="{ item }">
                    <app-text size="small">{{ item.userName }} ({{ item.userInternalIdNumber }})</app-text>
                </template>

                <!--Delete-->
                <template v-slot:item.delete="{ item }">
                    <app-icon @click.native="removeSteward(item)" class="cursorPointer pa-2" color="red" icon="delete" />
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
///////////////////////////////
//// ! Team Steward Form ! ////
///////////////////////////////

export default {
    name: 'TeamStewardForm',

    props: ['formData', 'isReadOnly', 'usersData'],

    data: () => ({
        tableHeaders: [
            {
                text: 'Name',
                value: 'userName',
                sortable: false,
                align: 'start'
            },
            {
                text: 'Actions',
                value: 'delete',
                sortable: false,
                align: 'center',
                width: '10px'
            }
        ],

        isStewardsEditable: false,
        tempTeamStewards: [],
        teamStewards: [],
        validationError: '',
        hasValidationErrors: false
    }),

    computed: {
        /**
         * Computed Init
         *
         * If there is any form data, assign it to the form.
         */
        computedInit() {
            const t = this
            const FORM_DATA = t.$props?.formData?.teamData

            if (FORM_DATA?.entityId) {
                // Assign all the users for this team
                t.teamStewards = t.$props.usersData.filter(
                    (u) => (u.userLevel === 'Steward-User' || u.userPayGrade === 'Senior Supervisor') && u.userTeams?.includes(FORM_DATA.entityId)
                )

                // Sort by userName
                t.teamStewards.sort((a, b) => (a.userName > b.userName ? 1 : -1))
            }
        },

        /**
         * Stewards Table Data
         *
         * Return an array of Stewards that are not already assigned to any team.
         * This ensures stewards can only be in one team at a time.
         *
         * @returns {array} array of available Stewards
         */
        computedStewardsTableData() {
            const t = this
            let usersData = t.$props.usersData

            // Get all the Stewards and Senior Supervisors that are not assigned to any team
            // or are only assigned to the current team
            usersData = usersData.filter((u) => {
                const isSteward = u.userLevel === 'Steward-User' || u.userPayGrade === 'Senior Supervisor'
                if (!isSteward) return false

                // Allow stewards with no teams or only on current team
                const hasNoTeams = !u.userTeams || u.userTeams.length === 0
                const isOnlyOnCurrentTeam = u.userTeams?.length === 1 && u.userTeams[0] === t.$props?.formData?.teamData?.entityId

                return hasNoTeams || isOnlyOnCurrentTeam
            })

            // Sort by userName
            usersData.sort((a, b) => (a.userName > b.userName ? 1 : -1))

            return usersData
        }
    },

    methods: {
        /**
         * Validate Steward Selection
         *
         * Ensure stewards being selected aren't already on other teams
         */
        validateStewardSelection() {
            const t = this
            const currentTeamId = t.$props?.formData?.teamData?.entityId

            // Check each selected steward
            const invalidStewards = t.tempTeamStewards.filter((steward) => {
                if (steward.userTeams?.length) {
                    return steward.userTeams.some((teamId) => teamId !== currentTeamId)
                }
                return false
            })

            if (invalidStewards.length) {
                t.validationError = `Some selected stewards are already assigned to other teams: ${invalidStewards.map((s) => s.userName).join(', ')}`
                t.hasValidationErrors = true
                return false
            }

            t.validationError = ''
            t.hasValidationErrors = false
            return true
        },

        /**
         * Edit Steward
         *
         * Toggle the editability.
         */
        editStewards() {
            const t = this
            t.isStewardsEditable = !t.isStewardsEditable
            if (t.isStewardsEditable) {
                t.validationError = ''
                t.hasValidationErrors = false
            }
        },

        /**
         * Reload Data
         *
         * Emit back to the parent (Teams.vue) to reload the data to update the page.
         */
        reloadData() {
            const t = this
            t.$emit('emitReloadData')
        },

        /**
         * Remove Steward
         *
         * Remove the Steward from the current Team by updating their userTeams array.
         * After removal, the steward will be available to be assigned to another team.
         *
         * @param userData the user object to remove
         * @returns {Promise<void>}
         */
        async removeSteward(userData) {
            const t = this
            const TEAM_ID = t.$props?.formData?.teamData?.entityId

            // Clear the userTeams array
            userData.userTeams = []

            // Update the user
            const USER_RESPONSE = await t.MIX_redis_update('user', userData.entityId, userData)

            // Handle any errors
            if (USER_RESPONSE.hasErrors) {
                console.error('Error updating User: ', USER_RESPONSE.error)
                return
            }

            // Clean up any EventUser documents for this user/team combination
            const EVENT_USER_RESPONSE = await t.$axios.post(`${process.env.VUE_APP_SERVER}/eventUser/cleanup/userTeam`, {
                userId: userData.entityId,
                teamId: TEAM_ID
            })

            if (EVENT_USER_RESPONSE.hasErrors) {
                console.error('Error cleaning up EventUser documents: ', EVENT_USER_RESPONSE.error)
                return
            }

            // Remove from temp array and update UI
            t.tempTeamStewards = t.tempTeamStewards.filter((ts) => ts.entityId !== userData.entityId)
            t.$sharedState.successMessage = 'Deleted Steward'
            t.reloadData()
        },

        /**
         * Save Stewards
         *
         * Send the new teamStewards array off to the server to update the list of Stewards on this team.
         * Each steward can only be assigned to one team.
         */
        async saveStewards(teamStewards) {
            const t = this
            const FORM_DATA = t.$props?.formData?.teamData

            // Validate before saving
            if (!t.validateStewardSelection()) {
                return
            }

            // If there are no Stewards, return
            if (!t.tempTeamStewards.length) return

            // Update Users
            const RESPONSE = await t.MIX_redis_updateUsersTeam(teamStewards, FORM_DATA.entityId)

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error updating User: ', RESPONSE.error)
                t.validationError = 'Error updating stewards. Please try again.'
                return
            }

            t.$sharedState.successMessage = 'Updated Stewards'
            t.isStewardsEditable = false
            t.validationError = ''
            t.hasValidationErrors = false
            t.reloadData()
        }
    }
}
</script>

<style scoped></style>
